import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { Truck, ChevronRight, Sparkles, AlertTriangle, BadgeCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import AiAnalysisCard from "@/components/AiAnalysisCard";

const TrailerWeightCalculatorPage = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const [vehicleKerb, setVehicleKerb] = useState(2200);  // SUV/pickup avg
  const [vehicleMax, setVehicleMax] = useState(3500);
  const [trailerEmpty, setTrailerEmpty] = useState(950);  // typical 2-horse iron trailer
  const [trailerMax, setTrailerMax] = useState(2700);
  const [horses, setHorses] = useState(2);
  const [horseAvgWeight, setHorseAvgWeight] = useState(550);
  const [equipment, setEquipment] = useState(150);

  useEffect(() => {
    document.title = isNl
      ? "Trailer Gewicht Calculator — welk rijbewijs heb je nodig? | HorseAppCompare"
      : "Trailer Weight Calculator — what license do you need? | HorseAppCompare";
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
    m.setAttribute("content", isNl
      ? "Bereken het totaalgewicht van auto + trailer + paard(en) en zie meteen of je B, B96, BE of C1E rijbewijs nodig hebt. EU-regels uitgelegd."
      : "Calculate the total weight of vehicle + trailer + horse(s) and instantly see whether you need a B, B96, BE or C1E license. EU rules explained.");
  }, [isNl]);

  const result = useMemo(() => {
    const horseLoad = horses * horseAvgWeight + equipment;
    const trailerLoaded = trailerEmpty + horseLoad;
    const trailerOverload = trailerLoaded > trailerMax;
    const combination = vehicleKerb + trailerLoaded;
    const combinationMax = vehicleMax + trailerMax;
    // EU license logic:
    let license = "B";
    let licenseLabel = "B";
    let licenseExplain = "";
    if (trailerMax <= 750 && combinationMax <= 3500) {
      license = "B"; licenseLabel = "B";
      licenseExplain = isNl ? "Trailer ≤ 750 kg of combinatie ≤ 3.500 kg → standaard B rijbewijs." : "Trailer ≤ 750 kg or combination ≤ 3,500 kg → standard B license.";
    } else if (combinationMax <= 4250) {
      license = "B96"; licenseLabel = "B96";
      licenseExplain = isNl ? "Combinatie 3.500-4.250 kg → B96 (B + verlenging na 1-daagse cursus, geen examen)." : "Combination 3,500-4,250 kg → B96 (B with extension after 1-day course, no exam).";
    } else if (trailerMax <= 3500 && combinationMax <= 7000) {
      license = "BE"; licenseLabel = "BE";
      licenseExplain = isNl ? "Trailer > 750 kg én combinatie 4.250-7.000 kg → BE rijbewijs (theorie + praktijkexamen)." : "Trailer > 750 kg and combination 4,250-7,000 kg → BE license (theory + practical exam).";
    } else {
      license = "C1E"; licenseLabel = "C1E";
      licenseExplain = isNl ? "Trailer > 3.500 kg of combinatie > 7.000 kg → C1E rijbewijs (extra medische keuring vereist)." : "Trailer > 3,500 kg or combination > 7,000 kg → C1E license (extra medical exam required).";
    }
    return { horseLoad, trailerLoaded, trailerOverload, combination, combinationMax, license, licenseLabel, licenseExplain };
  }, [vehicleKerb, vehicleMax, trailerEmpty, trailerMax, horses, horseAvgWeight, equipment, isNl]);

  const licenseColors = {
    B: "from-emerald-600 to-teal-700",
    B96: "from-blue-600 to-indigo-700",
    BE: "from-amber-600 to-orange-700",
    C1E: "from-red-600 to-rose-700",
  };

  return (
    <main data-testid="trailer-page">
      <section className="bg-gradient-to-br from-blue-50 via-white to-white border-b border-[#E2E8F0]">
        <div className="hac-container py-14 max-w-4xl">
          <nav className="flex items-center gap-2 text-xs text-[#64748B] mb-4"><Link to="/" className="hover:text-[#2563EB]">Home</Link><ChevronRight size={12} /><span className="text-[#0F172A] font-medium">{isNl ? "Trailer-gewicht" : "Trailer weight"}</span></nav>
          <div className="overline mb-2 text-blue-600 inline-flex items-center gap-1.5"><Truck size={12} /> {isNl ? "Gratis EU-tool" : "Free EU tool"}</div>
          <h1 className="font-heading text-4xl md:text-6xl text-[#0F172A] leading-[1.05] mb-3">{isNl ? "Trailer Gewicht Calculator" : "Trailer Weight Calculator"}</h1>
          <p className="text-lg text-[#64748B] leading-relaxed max-w-3xl">{isNl
            ? "Voer auto + trailer + paarden in. Zie direct of je B, B96, BE of C1E rijbewijs nodig hebt en of je niet over het maximumgewicht zit."
            : "Enter vehicle + trailer + horses. Instantly see whether you need a B, B96, BE or C1E license and whether you're under the maximum legal weight."}</p>
        </div>
      </section>

      <section className="hac-container py-10 max-w-6xl grid lg:grid-cols-[1fr_400px] gap-6">
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-6">
          <div>
            <h2 className="font-bold text-xl text-[#0F172A] mb-3">{isNl ? "Trekkend voertuig" : "Towing vehicle"}</h2>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="overline block mb-1 text-[#64748B]">{isNl ? "Leeg gewicht (kg)" : "Kerb weight (kg)"}</label><input type="number" className="hac-input" value={vehicleKerb} onChange={(e) => setVehicleKerb(parseInt(e.target.value || "0"))} data-testid="trailer-vehiclekerb" /></div>
              <div><label className="overline block mb-1 text-[#64748B]">{isNl ? "Max. toegestaan (kg)" : "Max permitted (kg)"}</label><input type="number" className="hac-input" value={vehicleMax} onChange={(e) => setVehicleMax(parseInt(e.target.value || "0"))} data-testid="trailer-vehiclemax" /></div>
            </div>
          </div>
          <div>
            <h2 className="font-bold text-xl text-[#0F172A] mb-3">{isNl ? "Trailer" : "Trailer"}</h2>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="overline block mb-1 text-[#64748B]">{isNl ? "Leeg gewicht (kg)" : "Empty weight (kg)"}</label><input type="number" className="hac-input" value={trailerEmpty} onChange={(e) => setTrailerEmpty(parseInt(e.target.value || "0"))} data-testid="trailer-trailerempty" /></div>
              <div><label className="overline block mb-1 text-[#64748B]">{isNl ? "Max. toegestaan (kg)" : "Max permitted (kg)"}</label><input type="number" className="hac-input" value={trailerMax} onChange={(e) => setTrailerMax(parseInt(e.target.value || "0"))} data-testid="trailer-trailermax" /></div>
            </div>
          </div>
          <div>
            <h2 className="font-bold text-xl text-[#0F172A] mb-3">{isNl ? "Lading" : "Load"}</h2>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="overline block mb-1 text-[#64748B]">{isNl ? "Aantal paarden" : "Horses"}</label><input type="number" min="0" max="4" className="hac-input" value={horses} onChange={(e) => setHorses(parseInt(e.target.value || "0"))} data-testid="trailer-horses" /></div>
              <div><label className="overline block mb-1 text-[#64748B]">{isNl ? "Gem. gewicht (kg)" : "Avg weight (kg)"}</label><input type="number" className="hac-input" value={horseAvgWeight} onChange={(e) => setHorseAvgWeight(parseInt(e.target.value || "0"))} data-testid="trailer-horseweight" /></div>
              <div><label className="overline block mb-1 text-[#64748B]">{isNl ? "Uitrusting (kg)" : "Equipment (kg)"}</label><input type="number" className="hac-input" value={equipment} onChange={(e) => setEquipment(parseInt(e.target.value || "0"))} data-testid="trailer-equipment" /></div>
            </div>
            <p className="text-[11px] text-[#94A3B8] mt-2">{isNl ? "Niet zeker van het paardengewicht? Gebruik onze " : "Not sure of horse weight? Use our "}<Link to={isNl ? "/paardengewicht-calculator" : "/horse-weight-calculator"} className="text-[#2563EB] hover:underline">{isNl ? "gewichtscalculator" : "weight calculator"}</Link>.</p>
          </div>
        </div>

        <div className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          <div className={`bg-gradient-to-br ${licenseColors[result.license]} text-white rounded-2xl p-6`}>
            <div className="text-xs uppercase tracking-widest text-white/70 mb-2 inline-flex items-center gap-1.5"><BadgeCheck size={12} /> {isNl ? "Vereist rijbewijs" : "Required license"}</div>
            <div className="text-6xl font-bold mb-2 tabular-nums" data-testid="trailer-license">{result.licenseLabel}</div>
            <p className="text-sm text-white/90 leading-relaxed">{result.licenseExplain}</p>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-2 text-sm">
            <h3 className="font-bold text-[#0F172A] mb-2 inline-flex items-center gap-2"><Sparkles size={16} className="text-blue-600" /> {isNl ? "Gewichten" : "Weights"}</h3>
            <div className="flex justify-between"><span>{isNl ? "Paarden + uitrusting" : "Horses + equipment"}</span><span className="font-mono">{result.horseLoad} kg</span></div>
            <div className={`flex justify-between ${result.trailerOverload ? "text-red-700 font-bold" : ""}`}><span>{isNl ? "Trailer beladen" : "Trailer loaded"}</span><span className="font-mono">{result.trailerLoaded} / {trailerMax} kg</span></div>
            <div className="flex justify-between border-t border-[#E2E8F0] pt-2 font-bold"><span>{isNl ? "Combinatie max (paper)" : "Combination max (paper)"}</span><span className="font-mono" data-testid="trailer-combo">{result.combinationMax} kg</span></div>
          </div>

          {result.trailerOverload && (
            <div className="bg-red-50 border border-red-300 rounded-xl p-4 text-xs">
              <p className="text-red-900 inline-flex items-start gap-2"><AlertTriangle size={14} className="text-red-600 shrink-0 mt-0.5" /><span>{isNl ? `Je trailer is overbeladen met ${result.trailerLoaded - trailerMax} kg. Overweeg 1 paard minder, lichtere uitrusting of een trailer met hogere max-belasting.` : `Trailer overloaded by ${result.trailerLoaded - trailerMax} kg. Consider one fewer horse, lighter equipment or a trailer with higher max payload.`}</span></p>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-xs">
            <p className="text-blue-900">{isNl ? "Let op: rijbewijs-regels verschillen licht per land. Boven gelden de standaard EU-regels (NL, BE, DE, FR, etc.). VK heeft sinds 2021 versoepelingen — check je nationale RDW." : "Note: license rules vary slightly per country. Above are standard EU rules (NL, BE, DE, FR, etc.). UK relaxed rules in 2021 — check your national authority."}</p>
          </div>

          <AiAnalysisCard tool="trailer" accent="blue" inputs={{
            vehicle_kerb: vehicleKerb,
            vehicle_max: vehicleMax,
            trailer_empty: trailerEmpty,
            trailer_max: trailerMax,
            horses: horses,
            horse_weight: horseAvgWeight,
            equipment: equipment,
            horse_load: result.horseLoad,
            license: result.licenseLabel,
            combo: result.combinationMax,
          }} />
        </div>
      </section>
    </main>
  );
};

export default TrailerWeightCalculatorPage;
